import { useMutation, useQuery } from '@apollo/client';
import {
  NOTIFICATION,
  PermissionEnum,
  useDialog,
  useNotifications,
  usePanel,
} from '@ecdlink/core';
import debounce from 'lodash.debounce';
import { HealthCareWorkerDto } from '@ecdlink/core/lib/models/dto/Users/health-care-worker.dto';
import {
  SendInviteToApplication,
  GetAllHealthCareWorker,
  GetAllPortalClinics,
  GetAllProvince,
  GetSubDistrictsAndStats,
  sentInviteToMultipleUsers,
  deleteMultipleUsers,
  getHealthCareWorkerCount,
} from '@ecdlink/graphql';
import {
  ActionModal,
  Dialog,
  DialogPosition,
  SearchDropDownOption,
  Table,
} from '@ecdlink/ui';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AlertModal from '../../../../components/dialog-alert/dialog-alert';
import { useUser } from '../../../../hooks/useUser';
import HealthCareWorkerPanelCreate from './components/health-care-worker-panel-create/health-care-worker-panel-create';
import { useHistory, useLocation } from 'react-router';
import { ConenctUsage } from '../team-leads/team-leads.types';
import { Status } from '../application-admins/applications-admins.types';
import { endOfDay, format, startOfDay } from 'date-fns';
import {
  AppVisitActivity,
  HcwRouteState,
  HealthCareWorkerRouteState,
} from './health-care-worker.types';
import ROUTES from '../../../../routes/app.routes-constants';
import { TableRefMethods } from '@ecdlink/ui/lib/components/table/types';
import { useUserRole } from '../../../../hooks/useUserRole';
import { columnColor } from '../../../../utils/health-care-worker/components-utils';

export const sortByConnectUsage: SearchDropDownOption<string>[] = [
  ConenctUsage?.InvitationActive,
  ConenctUsage?.InvitationExpired,
  ConenctUsage?.LastOnlineOver6Months,
  ConenctUsage?.LastOnlineWithinPast6Months,
  ConenctUsage?.Removed,
].map((item) => ({
  id: item,
  label: item,
  value: item,
}));

export const sortByClientStatusOptions: SearchDropDownOption<string>[] = [
  Status?.ACTIVE,
  Status?.INACTIVE,
].map((item) => ({
  id: item,
  label: item,
  value: item,
}));

export const sortByAppActivity: SearchDropDownOption<string>[] = [
  AppVisitActivity?.High,
  AppVisitActivity?.Medium,
  AppVisitActivity?.Low,
].map((item) => ({
  id: item,
  label: item,
  value: item,
}));

export default function HealthCareWorkers() {
  const { hasPermission } = useUser();
  const { isTeamLead, isAdministrator } = useUserRole();
  const { setNotification } = useNotifications();
  const dialog = useDialog();
  const [tableData, setTableData] = useState<any[]>([]);
  const history = useHistory();
  const [sendInviteToApplication] = useMutation(SendInviteToApplication);
  const panel = usePanel();
  const [searchValue, setSearchValue] = useState('');
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const pageSize = 20;

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [pendingEndDate, setPendingEndDate] = useState(null);
  const [handleAddUser, setHandleAddUser] = useState(false);
  const location = useLocation<HcwRouteState>();
  const [selectedCHWs, setSelectedCHWs] = useState<Irow[]>([]);
  const [stateClinicIds, setStateClinicIds] = useState([]); // these clinic ids are populated from the view of the team lead - modify filter to view users within clinics

  const isAllInactive = selectedCHWs.every((obj) => obj?.isActive === false);

  const registeredOrInactiveUsers = selectedCHWs?.filter(
    (item) => item?.isRegistered === true || item?.isActive === false
  );
  const disableInviteBulkAction =
    selectedCHWs?.length <= registeredOrInactiveUsers?.length;

  // INFO: It’s being filtered by phoneNumber, because if the user doesn’t have the phoneNumber field filled, it’s breaking the sendInvitation mutation
  const chwIdsToSendInvitation = selectedCHWs
    ?.filter((item) => !item?.isRegistered && item?.user?.phoneNumber)
    ?.map((item) => item?.id);

  const [sendInvitations, { loading: invitationsLoading }] = useMutation(
    sentInviteToMultipleUsers,
    {
      variables: {
        userIds: [],
      },
      fetchPolicy: 'network-only',
    }
  );
  const [deactivateUsers, { loading: deactivating }] = useMutation(
    deleteMultipleUsers,
    {
      variables: {
        ids: [],
      },
      fetchPolicy: 'network-only',
    }
  );

  const tableRef = useRef<TableRefMethods>(null);

  const onChange = (dates) => {
    const [start, end] = dates;
    setPendingStartDate(start);
    setPendingEndDate(end);
  };

  const openDatePicker = () => {
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
    setIsDatePickerOpen(true);
  };

  const applyDateFilter = () => {
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setIsDatePickerOpen(false);
  };

  const cancelDateFilter = () => {
    setIsDatePickerOpen(false);
  };

  const [provinces, setProvinces] = useState<SearchDropDownOption<string>[]>(
    []
  );
  const [provincesFiltered, setProvincesFiltered] = useState<
    SearchDropDownOption<string>[]
  >([]);
  const filteredProvinces = useMemo(
    () => provincesFiltered?.map((item) => item?.id),
    [provincesFiltered]
  );

  const [clinics, setClinics] = useState<SearchDropDownOption<string>[]>([]);
  const [clinicsFiltered, setClinicsFiltered] = useState<
    SearchDropDownOption<string>[]
  >([]);
  const filteredClinics = useMemo(
    () => clinicsFiltered?.map((item) => item?.label),
    [clinicsFiltered]
  );

  const [subDistricts, setSubDistricts] =
    useState<SearchDropDownOption<string>[]>();
  const [subDistrictsFiltered, setSubDistrictsFiltered] = useState<
    SearchDropDownOption<string>[]
  >([]);
  const filteredSubDistricts = useMemo(
    () => subDistrictsFiltered?.map((item) => item?.id),
    [subDistrictsFiltered]
  );

  const [statusFilter, setStatusFilter] = useState<
    SearchDropDownOption<string>[]
  >(isTeamLead ? [] : [sortByClientStatusOptions[0]]);

  const [connectUsageFilter, setConnectUsageFilter] = useState<
    SearchDropDownOption<string>[]
  >([]);
  const filteredConnectUsage = useMemo(
    () => connectUsageFilter?.map((item) => item?.id),
    [connectUsageFilter]
  );

  const [appActivityFilter, setAppActivityFilter] = useState<
    SearchDropDownOption<string>[]
  >([]);
  const filteredAppActivity = useMemo(
    () => appActivityFilter?.map((item) => item?.id),
    [appActivityFilter]
  );

  const viewSelectedRow = (selectedRow: any) => {
    const user = tableData?.find(
      (item) => item?.userId === selectedRow?.userId
    );

    localStorage.setItem(
      'selectedUser',
      selectedRow?.userId ?? selectedRow?.id
    );
    history.push({
      pathname: ROUTES.VIEW_USERS,
      state: {
        component: 'chw',
        userId: selectedRow?.userId,
        clinicId: selectedRow?.clinicId,
        hcwId: selectedRow?.id,
        isRegistered: selectedRow?.isRegistered,
        connectUsage: selectedRow?.connectUsage,
        connectUsageColor: user?.user?.connectUsageColor,
      },
    });
  };

  const isActiveFilter = useMemo(() => {
    if (statusFilter?.length === 1) {
      return statusFilter[0]?.value === Status?.ACTIVE;
    }
    return null;
  }, [statusFilter]);

  const queryVariables = useMemo(
    () => ({
      search: searchValue,
      clinicSearch: filteredClinics,
      provinceSearch: filteredProvinces,
      subDistrictSearch: filteredSubDistricts,
      visitSearch: filteredAppActivity,
      connectUsageSearch: filteredConnectUsage,
      insertedDateFrom: startDate ? startOfDay(startDate).toISOString() : null,
      insertedDateTo: endDate ? endOfDay(endDate).toISOString() : null,
      isActive: isActiveFilter,
      pagingInput: {
        pageNumber: selectedPage,
        pageSize,
      },
      order: [
        {
          insertedDate: 'DESC',
        },
      ],
    }),
    [
      searchValue,
      filteredAppActivity,
      filteredClinics,
      filteredConnectUsage,
      filteredProvinces,
      filteredSubDistricts,
      startDate,
      endDate,
      isActiveFilter,
      selectedPage,
    ]
  );

  const { data, refetch, loading } = useQuery(GetAllHealthCareWorker, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });

  const { data: countData } = useQuery(getHealthCareWorkerCount, {
    variables: {
      search: searchValue,
      clinicSearch: filteredClinics,
      provinceSearch: filteredProvinces,
      subDistrictSearch: filteredSubDistricts,
      visitSearch: filteredAppActivity,
      connectUsageSearch: filteredConnectUsage,
      insertedDateFrom: startDate ? startOfDay(startDate).toISOString() : null,
      insertedDateTo: endDate ? endOfDay(endDate).toISOString() : null,
      isActive: isActiveFilter,
    },
    fetchPolicy: 'network-only',
  });
  const totalRows: number = countData?.countHealthCareWorkers ?? 0;

  // Reset to page 1 whenever a filter (anything other than the page itself) changes, so the
  // user doesn't land on a now out-of-range page with stale/empty results.
  useEffect(() => {
    setSelectedPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchValue,
    filteredAppActivity,
    filteredClinics,
    filteredConnectUsage,
    filteredProvinces,
    filteredSubDistricts,
    startDate,
    endDate,
    isActiveFilter,
  ]);

  useEffect(() => {
    history.replace({
      pathname: location.pathname,
      state: { queryVariables: queryVariables } as HealthCareWorkerRouteState,
    });
  }, [history, location.pathname, queryVariables]);

  const { data: clinicData, loading: loadingClinics } = useQuery(
    GetAllPortalClinics,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        subDistrictIds: filteredSubDistricts,
        provinceIds: filteredProvinces,
      },
    }
  );
  const { data: provinceData, loading: loadingProvince } = useQuery(
    GetAllProvince,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: subDistrictData, loading: loadingDistricts } = useQuery(
    GetSubDistrictsAndStats,
    {
      fetchPolicy: 'cache-and-network',
      variables: { provinceIds: filteredProvinces },
    }
  );

  const isLoading =
    loading || loadingClinics || loadingProvince || loadingDistricts;

  const isFilterActive =
    !!connectUsageFilter?.length ||
    !!appActivityFilter?.length ||
    !!startDate ||
    !!endDate ||
    !!clinicsFiltered?.length ||
    !!subDistrictsFiltered?.length ||
    !!provincesFiltered?.length ||
    !!statusFilter?.length;

  const noContentText = useMemo(() => {
    if (isFilterActive) {
      return 'No results found. Try changing the filters selected';
    }

    if (isTeamLead) {
      return "You don't have any CHWs yet! Contact Grow Great for more information";
    }

    return 'No entries found';
  }, [isFilterActive, isTeamLead]);

  const search = useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value || '');
      }, 400),
    []
  );

  const mapUserTableItem = (item: any) => {
    return {
      ...item,
      displayColumnIdPassportEmail:
        item?.user?.userName ?? item?.idNumber ?? item?.user?.email ?? '',
      userId: item.user?.id,
      fullName: `${item.user?.fullName}`,
      isActive: item.user?.isActive,
      idNumber: item.user?.idNumber,
      dateInvited: item.user?.insertedDate,
      connectUsage: item?.user?.connectUsage,
    };
  };

  useEffect(() => {
    if (data && data.allHealthCareWorkers) {
      const copyItems = data.allHealthCareWorkers.map(
        (item: HealthCareWorkerDto) => mapUserTableItem(item)
      );

      setTableData(copyItems);
    }
  }, [data]);

  const displayPanel = () => {
    panel({
      noPadding: true,
      title: '',
      render: (onSubmit: any) => (
        <HealthCareWorkerPanelCreate
          key={`userPanelCreate`}
          closeDialog={(userCreated: boolean) => {
            onSubmit();

            if (userCreated) {
              refetch();
            }
          }}
        />
      ),
    });
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setConnectUsageFilter([]);
    setProvincesFiltered([]);
    setClinicsFiltered([]);
    setStartDate('');
    setEndDate('');
    setPendingStartDate(null);
    setPendingEndDate(null);
    setIsDatePickerOpen(false);
    setAppActivityFilter([]);
    setSubDistrictsFiltered([]);
  };

  useEffect(() => {
    if (clinicData?.allPortalClinics?.length > 0) {
      const clinicsSorted = clinicData?.allPortalClinics
        ?.slice()
        ?.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

      setClinics(
        clinicsSorted?.map((item) => {
          return {
            value: item?.id,
            label: item?.name,
            id: item?.id,
          };
        })
      );

      if (location?.state?.clinicIds && location?.state?.clinicIds.length > 0) {
        setStateClinicIds(location?.state?.clinicIds);
      }
    }
  }, [
    clinicData?.allPortalClinics,
    location?.state?.clinicIds,
    stateClinicIds,
  ]);

  useEffect(() => {
    if (stateClinicIds && stateClinicIds.length > 0 && clinics.length > 0) {
      var tlCinics = clinics.filter(function (item) {
        return stateClinicIds.indexOf(item.id) !== -1;
      });
      setClinicsFiltered(tlCinics);
    }
  }, [clinics, stateClinicIds]);

  useEffect(() => {
    if (subDistrictData?.subDistrictsAndStats?.length > 0) {
      const subDistrictSorted = subDistrictData?.subDistrictsAndStats
        ?.slice()
        ?.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
      setSubDistricts(
        subDistrictSorted?.map((item) => {
          return {
            value: item?.id,
            label: item?.name,
            id: item?.id,
          };
        })
      );
    }
  }, [subDistrictData]);

  useEffect(() => {
    if (provinceData?.GetAllProvince?.length > 0) {
      const provincesSorted = provinceData?.GetAllProvince?.slice()?.sort(
        (a, b) =>
          a.description < b.description
            ? -1
            : a.description > b.description
            ? 1
            : 0
      );

      setProvinces(
        provincesSorted
          ?.filter((prov) => prov?.description !== 'N/A')
          ?.map((item) => {
            return {
              value: item?.id,
              label: item?.description,
              id: item?.id,
            };
          })
      );
    }
  }, [provinceData?.GetAllProvince]);

  const handleResetSelectedRows = () => {
    tableRef?.current?.resetSelectedRows();
  };

  const columns: Icolumn[] = [
    {
      field: 'displayColumnIdPassportEmail',
      use: 'id / Passport',
    },
    {
      field: 'fullName',
      use: 'name',
    },
    {
      field: 'connectUsageComponent',
      use: 'CHW Connect usage',
    },
    {
      field: 'insertedDateFormatted',
      use: 'Date invited',
    },
    {
      field: 'isActiveComponent',
      use: 'Active',
    },
  ];

  const rows: Irow[] =
    tableData?.map((item) => ({
      ...item,
      key: item?.id,
      displayColumnIdPassportEmail:
        item?.user?.userName ?? item?.idNumber ?? item?.user?.email ?? '-',
      fullName: `${item?.user?.firstName} ${item?.user?.surname}`,
      connectUsageComponent: item?.user?.connectUsage
        ? columnColor(item.user.connectUsage)
        : '-',
      insertedDateFormatted: item?.user?.insertedDate
        ? format(new Date(item?.user?.insertedDate), 'dd/MM/yyyy')
        : '-',
      isActiveComponent: (
        <p
          className={
            item?.user?.isActive ? 'text-successMain' : 'text-errorMain'
          }
        >
          {item?.user?.isActive ? 'Active' : 'Inactive'}
        </p>
      ),
    })) ?? [];

  // INFO: Functions from src/admin-portal/src/app/pages/users/sub-pages/health-care-worker/components/ui-table
  const inviteUsers = useCallback(() => {
    sendInvitations({
      variables: {
        userIds: chwIdsToSendInvitation,
      },
    })
      .then((res) => {
        if (res.data?.sendBulkInviteToPortal?.success.length > 0) {
          setNotification({
            title: ` Successfully Sent ${res.data?.sendBulkInviteToPortal?.success.length} Invites!`,
            variant: NOTIFICATION.SUCCESS,
          });
          setSelectedCHWs([]);
          handleResetSelectedRows();
          refetch();
        }
        if (res.data?.sendBulkInviteToPortal?.failed.length > 0) {
          setNotification({
            title: ` Failed to Send to ${res.data?.sendBulkInviteToPortal?.failed.length} Users!`,
            variant: NOTIFICATION.ERROR,
          });
          setSelectedCHWs([]);
          handleResetSelectedRows();
        }
      })
      .catch((err) => {
        setNotification({
          title: 'Failed to send invitations',
          variant: NOTIFICATION.ERROR,
        });
      });
  }, [chwIdsToSendInvitation, refetch, sendInvitations, setNotification]);

  const deactivateUser = useCallback(() => {
    deactivateUsers({
      variables: {
        ids: selectedCHWs?.map((item) => item?.id),
      },
    })
      .then((res) => {
        if (res.data?.bulkDeleteUser?.success.length > 0) {
          setNotification({
            title: ` Successfully Deactivated ${res.data?.bulkDeleteUser?.success.length} Users!`,
            variant: NOTIFICATION.SUCCESS,
          });
          refetch();
          setSelectedCHWs([]);
          handleResetSelectedRows();
        }
        if (res.data?.bulkDeleteUser?.failed.length > 0) {
          setNotification({
            title: ` Failed to Deactivate ${res.data?.bulkDeleteUser?.failed.length} Users!`,
            variant: NOTIFICATION.ERROR,
          });
          setSelectedCHWs([]);
          handleResetSelectedRows();
        }
      })
      .catch((err) => {
        setNotification({
          title: 'Failed to deactivate',
          variant: NOTIFICATION.ERROR,
        });
      });
  }, [deactivateUsers, selectedCHWs, setNotification, refetch]);

  const handleBulkDelete = useCallback(() => {
    dialog({
      position: DialogPosition.Middle,
      render: (onClose) => (
        <AlertModal
          title={`Deactivate ${
            selectedCHWs?.length - registeredOrInactiveUsers?.length
          } CHWs?`}
          message={`Are you sure you want to deactivate these CHWs? CHWs will lose their access to CHW Connect immediately. Make sure you have communicated this to CHWs before deactivating them.`}
          btnText={['Yes, deactivate CHWs', 'No, Cancel']}
          hasAlert={isAllInactive || registeredOrInactiveUsers?.length > 0}
          alertMessage={`Note: ${registeredOrInactiveUsers?.length} CHWs selected have already been deactivated.`}
          alertType="error"
          onCancel={() => {
            onClose();
            setSelectedCHWs([]);
            handleResetSelectedRows();
          }}
          onSubmit={() => {
            deactivateUser();
            onClose();
          }}
        />
      ),
    });
  }, [
    deactivateUser,
    dialog,
    isAllInactive,
    registeredOrInactiveUsers?.length,
    selectedCHWs?.length,
  ]);

  const handleBulkInvitation = useCallback(() => {
    dialog({
      position: DialogPosition.Middle,
      render: (onClose) => (
        <AlertModal
          title={`Resend invitation to ${
            selectedCHWs?.length - registeredOrInactiveUsers?.length
          } CHWs?`}
          message={`Are you sure you want to send the invitation to the ${
            selectedCHWs?.length - registeredOrInactiveUsers?.length
          } CHWs selected?`}
          btnText={['Yes, resend', 'No, Cancel']}
          hasAlert={isAllInactive || registeredOrInactiveUsers?.length > 0}
          alertMessage={`Note: ${registeredOrInactiveUsers?.length} selected CHWs are already registered or have been deactivated so you cannot resend these invitations.`}
          alertType="error"
          onCancel={() => {
            onClose();
            setSelectedCHWs([]);
            handleResetSelectedRows();
          }}
          onSubmit={() => {
            inviteUsers();
            onClose();
          }}
        />
      ),
    });
  }, [
    dialog,
    selectedCHWs?.length,
    isAllInactive,
    registeredOrInactiveUsers?.length,
    inviteUsers,
  ]);

  return (
    <>
      <div className="bg-adminPortalBg h-full rounded-2xl p-4 ">
        <div className="rounded-xl bg-white p-12">
          <Table
            ref={tableRef}
            rows={rows}
            watchMode
            columns={columns}
            totalRows={totalRows}
            currentPage={selectedPage}
            rowsPerPage={pageSize}
            onChangePage={(page) => setSelectedPage(Math.max(page, 1))}
            onClearFilters={clearFilters}
            onChangeSelectedRows={setSelectedCHWs}
            onClickRow={viewSelectedRow}
            noContentText={noContentText}
            loading={{
              isLoading: tableData === undefined || isLoading,
              size: 'medium',
              spinnerColor: 'adminPortalBg',
              backgroundColor: 'secondary',
            }}
            actionButton={
              hasPermission(PermissionEnum.create_user) &&
              (!isTeamLead || isAdministrator)
                ? {
                    text: 'Add CHWs',
                    onClick: () => setHandleAddUser(true),
                    icon: 'PlusIcon',
                  }
                : undefined
            }
            search={{
              placeholder: 'Search by id number or name...',
              onChange: search,
            }}
            bulkActions={[
              {
                type: 'filled',
                color: 'secondary',
                textColor: 'white',
                text: 'Resend Invitations',
                icon: 'PaperAirplaneIcon',
                isLoading: invitationsLoading,
                disabled:
                  invitationsLoading ||
                  disableInviteBulkAction ||
                  isAllInactive,
                onClick: handleBulkInvitation,
              },
              {
                type: 'outlined',
                color: 'tertiary',
                textColor:
                  deactivating || isAllInactive ? 'uiLight' : 'tertiary',
                icon: 'TrashIcon',
                text: 'Deactivate User',
                isLoading: deactivating,
                disabled: deactivating || isAllInactive,
                onClick: handleBulkDelete,
              },
            ]}
            filters={[
              {
                type: 'search-dropdown',
                menuItemClassName: 'w-11/12 mx-8 mt-1',
                options: sortByConnectUsage,
                selectedOptions: connectUsageFilter,
                onChange: setConnectUsageFilter,
                placeholder: 'CHW Connect usage',
                multiple: true,
                info: { name: 'CHW Connect usage:' },
                overlayTopOffset: isTeamLead ? '100' : '177',
              },
              {
                dateFormat: 'd MMM yyyy',
                hideFilter: isTeamLead,
                className: 'w-64 h-11 mt-1 border-2 border-transparent',
                isFullWidth: false,
                colour: !!startDate ? 'secondary' : 'adminPortalBg',
                textColour: !!startDate ? 'white' : 'textMid',
                placeholderText: 'Date invited',
                type: 'date-picker',
                showChevronIcon: true,
                chevronIconColour: !!startDate ? 'white' : 'primary',
                hideCalendarIcon: true,
                selected: pendingStartDate,
                onChange,
                startDate: pendingStartDate,
                endDate: pendingEndDate,
                selectsRange: true,
                open: isDatePickerOpen,
                onInputClick: openDatePicker,
                onClickOutside: cancelDateFilter,
                shouldCloseOnSelect: false,
                monthsShown: 2,
                calendarClassName: 'health-care-worker-date-range-picker',
                children: (
                  <>
                    <div className="w-full"></div>
                    {/* Add this first div to align buttons correctly */}
                    <div className="flex justify-end gap-2 p-2">
                      <button
                        onClick={cancelDateFilter}
                        type="button"
                        className="text-textMid outline-none inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={applyDateFilter}
                        disabled={!pendingStartDate || !pendingEndDate}
                        type="button"
                        className="bg-secondary hover:bg-uiMid outline-none inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Apply
                      </button>
                    </div>
                  </>
                ),
              },
              {
                type: 'search-dropdown',
                menuItemClassName: 'w-11/12 mx-8 mt-1',
                options: sortByAppActivity,
                selectedOptions: appActivityFilter,
                onChange: setAppActivityFilter,
                placeholder: 'App visit activity',
                multiple: true,
                info: { name: 'App visit activity:' },
              },
              {
                type: 'search-dropdown',
                menuItemClassName: 'w-11/12 mx-8 mt-1',
                hideFilter: isTeamLead,
                options: provinces,
                selectedOptions: provincesFiltered,
                onChange: setProvincesFiltered,
                placeholder: 'Province',
                multiple: true,
                info: { name: 'Province:' },
              },
              {
                type: 'search-dropdown',
                menuItemClassName: 'w-11/12 mx-8 mt-1',
                hideFilter: isTeamLead,
                options: subDistricts,
                selectedOptions: subDistrictsFiltered,
                onChange: setSubDistrictsFiltered,
                placeholder: 'Sub-district',
                multiple: true,
                info: { name: 'Sub-district:' },
                showSearch: true,
              },
              {
                type: 'search-dropdown',
                menuItemClassName: 'w-11/12 mx-8 mt-1',
                options: clinics,
                selectedOptions: clinicsFiltered,
                onChange: setClinicsFiltered,
                placeholder: 'Clinic',
                multiple: true,
                showSearch: true,
                info: { name: 'Clinic:' },
              },
              {
                type: 'search-dropdown',
                menuItemClassName: 'w-11/12 mx-8 mt-1',
                hideFilter: isTeamLead,
                options: sortByClientStatusOptions,
                selectedOptions: statusFilter,
                onChange: setStatusFilter,
                placeholder: 'Status',
                multiple: true,
                info: { name: 'Status:' },
                overlayTopOffset: isTeamLead ? '100' : '177',
              },
            ]}
          />
        </div>
      </div>
      <Dialog
        className="p-20"
        stretch
        visible={handleAddUser}
        position={DialogPosition.Middle}
      >
        <ActionModal
          className="z-80"
          icon={'ExclamationCircleIcon'}
          iconColor="white"
          iconBorderColor="infoMain"
          importantText={`Would you like to add one CHW or multiple?`}
          actionButtons={[
            {
              text: 'Add multiple CHWs',
              textColour: 'white',
              colour: 'secondary',
              type: 'filled',
              onClick: () =>
                history.push({
                  pathname: ROUTES.UPLOAD_USERS,
                }),
              leadingIcon: 'UsersIcon',
            },
            {
              text: 'Add one CHW',
              textColour: 'secondary',
              colour: 'secondary',
              type: 'outlined',
              onClick: () => {
                displayPanel();
                setHandleAddUser(false);
              },
              leadingIcon: 'UserIcon',
            },
          ]}
        />
      </Dialog>
    </>
  );
}
